import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone, Mail, MapPin, Clock } from 'lucide-react';

const LOGO_URL = '/npm-logo.svg';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Testimonials', path: '/testimonials' },
  { name: 'Data Recovery', path: '/data-recovery' },
  { name: 'Connect', path: '/connect' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/75 backdrop-blur-sm border-b border-gray-100" data-testid="header">
      <div className="site-container">
        <div className="flex items-center justify-between h-[60px] lg:h-[68px]">
          {/* Logo - larger without tagline */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="NPM Computer Sales & Service" 
              className="h-14 lg:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation - tighter spacing */}
          <nav className="hidden lg:flex items-center" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'nav-link-active' : ''}`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <Button 
              asChild 
              className="bg-[#7dc242] hover:bg-[#6ba83a] hover:shadow-md text-white border-0 h-10 px-5 text-[0.9375rem] font-medium transition-all"
              data-testid="header-cta"
            >
              <a href="tel:+17027695422">
                <Phone className="w-4 h-4 mr-2" />
                Schedule an Assessment
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#7dc242] focus:ring-offset-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 mobile-menu-enter" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-3 text-[0.9375rem] font-medium rounded-md transition-colors ${
                    location.pathname === link.path 
                      ? 'text-[#7dc242] bg-green-50 font-semibold' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 mt-2 border-t border-gray-100">
                <Button 
                  asChild 
                  className="w-full bg-[#7dc242] hover:bg-[#6ba83a] text-white border-0"
                >
                  <a href="tel:+17027695422">
                    <Phone className="w-4 h-4 mr-2" />
                    Schedule an Assessment
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#3f3f3f] text-white" data-testid="footer">
      <div className="site-container py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
          {/* Company Info */}
          <div className="lg:col-span-2">
            {/* Logo in white tile container */}
            <div className="inline-block bg-white p-3 rounded-lg border border-gray-200/20 mb-4">
              <img 
                src={LOGO_URL} 
                alt="NPM Computer Sales & Service" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-white font-bold text-base mb-2">
              NPM Computer Sales & Service
            </p>
            <p className="text-gray-400 text-[0.9375rem] leading-relaxed max-w-md">
              Business technology sales, deployment, managed IT, and cybersecurity support for organizations across Las Vegas and Henderson.
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 text-gray-300">Contact</h3>
            <ul className="space-y-3 text-[0.9375rem]">
              <li>
                <a 
                  href="tel:+17027695422" 
                  className="flex items-start gap-3 text-gray-400 hover:text-[#7dc242] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  (702) 769-5422
                </a>
              </li>
              <li>
                <a 
                  href="mailto:david@applevar.com" 
                  className="flex items-start gap-3 text-gray-400 hover:text-[#7dc242] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  david@applevar.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Mon–Fri, 8am–5pm
              </li>
            </ul>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 text-gray-300">Location</h3>
            <address className="not-italic text-[0.9375rem] text-gray-400">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p>NPM Computer Sales & Service, Inc.</p>
                  <p>1575 Cattle Ranch Place</p>
                  <p>Henderson, NV 89002</p>
                </div>
              </div>
            </address>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} NPM Computer Sales & Service, Inc. All rights reserved.
            </p>
            <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm text-gray-500 hover:text-[#7dc242] transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
